import { createContext, useContext, useState, useEffect } from 'react'

const ThemeContext = createContext()

export const useTheme = () => {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within ThemeProvider')
    }
    return context
}

export const themes = {
    default: {
        name: 'Mặc định',
        icon: '🌟',
        description: 'Giao diện mặc định của DelyTour',
        colors: {
            primary: '#2563eb',
            primaryHover: '#1d4ed8',
            secondary: '#10b981',
            accent: '#f59e0b',
            background: '#ffffff',
            text: '#1f2937',
            textLight: '#6b7280',
            // Gradient backgrounds
            bgGradientFrom: '#ffffff',
            bgGradientTo: '#ffffff',
            cardBg: '#ffffff',
            buttonGradientFrom: '#2563eb',
            buttonGradientTo: '#7c3aed'
        }
    },
    spring: {
        name: 'Xuân Tươi',
        icon: '🌸',
        description: 'Sáng tươi xanh như mùa xuân',
        colors: {
            primary: '#10b981',
            primaryHover: '#059669',
            secondary: '#84cc16',
            accent: '#fbbf24',
            background: '#f0fdf4',
            text: '#064e3b',
            textLight: '#065f46',
            // Spring gradient - light green to mint
            bgGradientFrom: '#ecfdf5',
            bgGradientTo: '#d1fae5',
            cardBg: '#ffffff',
            buttonGradientFrom: '#10b981',
            buttonGradientTo: '#34d399'
        }
    },
    summer: {
        name: 'Hạ Rực Rỡ',
        icon: '☀️',
        description: 'Rực rỡ ánh vàng mùa hè',
        colors: {
            primary: '#f59e0b',
            primaryHover: '#d97706',
            secondary: '#ef4444',
            accent: '#fb923c',
            background: '#fffbeb',
            text: '#78350f',
            textLight: '#92400e',
            // Summer gradient - yellow to orange
            bgGradientFrom: '#fef3c7',
            bgGradientTo: '#fed7aa',
            cardBg: '#ffffff',
            buttonGradientFrom: '#f59e0b',
            buttonGradientTo: '#ef4444'
        }
    },
    autumn: {
        name: 'Thu Mát Mẻ',
        icon: '🍂',
        description: 'Mát mẻ dịu dàng mùa thu',
        colors: {
            primary: '#ea580c',
            primaryHover: '#c2410c',
            secondary: '#ca8a04',
            accent: '#dc2626',
            background: '#fff7ed',
            text: '#7c2d12',
            textLight: '#9a3412',
            // Autumn gradient - orange to brown
            bgGradientFrom: '#ffedd5',
            bgGradientTo: '#fecaca',
            cardBg: '#ffffff',
            buttonGradientFrom: '#ea580c',
            buttonGradientTo: '#dc2626'
        }
    },
    winter: {
        name: 'Đông Lạnh Giá',
        icon: '❄️',
        description: 'Lạnh giá trong trẻo mùa đông',
        colors: {
            primary: '#0ea5e9',
            primaryHover: '#0284c7',
            secondary: '#06b6d4',
            accent: '#8b5cf6',
            background: '#f0f9ff',
            text: '#0c4a6e',
            textLight: '#075985',
            // Winter gradient - light blue to ice blue
            bgGradientFrom: '#e0f2fe',
            bgGradientTo: '#ddd6fe',
            cardBg: '#ffffff',
            buttonGradientFrom: '#0ea5e9',
            buttonGradientTo: '#8b5cf6'
        }
    }
}

export const ThemeProvider = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState('default')
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        // Load theme from Firestore settings
        const loadTheme = async () => {
            try {
                const { collection, getDocs } = await import('firebase/firestore')
                const { db } = await import('../lib/firebase')

                const settingsSnapshot = await getDocs(collection(db, 'settings'))
                if (!settingsSnapshot.empty) {
                    const settings = settingsSnapshot.docs[0].data()
                    if (settings.theme && themes[settings.theme]) {
                        setCurrentTheme(settings.theme)
                    }
                }
            } catch (error) {
                console.error('Error loading theme:', error)
            } finally {
                setLoading(false)
            }
        }

        loadTheme()
    }, [])

    useEffect(() => {
        // Apply theme colors to CSS variables
        const theme = themes[currentTheme]
        if (theme) {
            const root = document.documentElement
            root.style.setProperty('--color-primary', theme.colors.primary)
            root.style.setProperty('--color-primary-hover', theme.colors.primaryHover)
            root.style.setProperty('--color-secondary', theme.colors.secondary)
            root.style.setProperty('--color-accent', theme.colors.accent)
            root.style.setProperty('--color-background', theme.colors.background)
            root.style.setProperty('--color-text', theme.colors.text)
            root.style.setProperty('--color-text-light', theme.colors.textLight)

            // Apply gradient colors
            root.style.setProperty('--color-bg-gradient-from', theme.colors.bgGradientFrom)
            root.style.setProperty('--color-bg-gradient-to', theme.colors.bgGradientTo)
            root.style.setProperty('--color-card-bg', theme.colors.cardBg)
            root.style.setProperty('--color-button-gradient-from', theme.colors.buttonGradientFrom)
            root.style.setProperty('--color-button-gradient-to', theme.colors.buttonGradientTo)
        }
    }, [currentTheme])

    const changeTheme = async (themeName) => {
        if (themes[themeName]) {
            setCurrentTheme(themeName)

            // Save to Firestore settings
            try {
                const { collection, getDocs, updateDoc, setDoc, doc } = await import('firebase/firestore')
                const { db } = await import('../lib/firebase')

                const settingsSnapshot = await getDocs(collection(db, 'settings'))
                if (!settingsSnapshot.empty) {
                    const settingsDoc = settingsSnapshot.docs[0]
                    await updateDoc(doc(db, 'settings', settingsDoc.id), {
                        theme: themeName,
                        updatedAt: new Date()
                    })
                } else {
                    // Create settings if not exists
                    await setDoc(doc(collection(db, 'settings')), {
                        theme: themeName,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    })
                }
            } catch (error) {
                console.error('Error saving theme:', error)
            }
        }
    }

    const value = {
        currentTheme,
        theme: themes[currentTheme],
        themes,
        changeTheme,
        loading
    }

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    )
}

